import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Tooltip,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { doc, updateDoc } from "firebase/firestore";
import { updateEmail } from "firebase/auth";
import { db, auth } from "../firebase";
import { UserAuth } from "../contexts/AuthContext";

const ProfileEditModal = ({ isOpen, onClose }) => {
  const { user } = UserAuth();
  const toast = useToast();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const isGoogleUser = user?.signInMethod === "google";

  useEffect(() => {
    if (isOpen && user) {
      setFirstName(user.firstName || "");
      setLastName(user.lastName || "");
      setEmail(user.email || "");
    }
  }, [isOpen, user]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!user) return;

    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();
    const trimmedEmail = email.trim();

    if (!trimmedFirst || !trimmedLast) {
      toast({
        title: "Name required",
        description: "First and last name cannot be empty.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setIsSaving(true);
    try {
      const emailChanged = trimmedEmail !== user.email;

      if (emailChanged && !isGoogleUser) {
        await updateEmail(auth.currentUser, trimmedEmail);
      }

      const updates = {
        firstName: trimmedFirst,
        lastName: trimmedLast,
      };
      if (emailChanged && !isGoogleUser) {
        updates.email = trimmedEmail;
      }
      await updateDoc(doc(db, "users", user.uid), updates);

      toast({
        title: "Profile updated",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      onClose();
    } catch (error) {
      let description = error.message;
      if (error.code === "auth/requires-recent-login") {
        description =
          "For security, please log out and log back in before changing your email.";
      } else if (error.code === "auth/email-already-in-use") {
        description = "That email is already in use by another account.";
      } else if (error.code === "auth/invalid-email") {
        description = "Please enter a valid email address.";
      } else if (error.code === "auth/operation-not-allowed") {
        description =
          "Email changes are temporarily unavailable. Please contact support.";
      }
      toast({
        title: "Could not update profile",
        description,
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit profile</ModalHeader>
        <ModalCloseButton />
        <form onSubmit={handleSave}>
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel>First name</FormLabel>
                <Input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Last name</FormLabel>
                <Input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Email</FormLabel>
                {isGoogleUser ? (
                  <Tooltip
                    label="Managed by your Google account"
                    hasArrow
                    placement="top"
                  >
                    <Input value={email} isReadOnly bg="gray.100" />
                  </Tooltip>
                ) : (
                  <>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                    <FormHelperText>
                      Changing your email signs you out of other sessions.
                    </FormHelperText>
                  </>
                )}
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button variant="secondary" type="submit" isLoading={isSaving}>
              Save
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default ProfileEditModal;
